import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ChevronRight, Loader2, CheckCircle2, AlertCircle, ShieldCheck } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import ArtworkUpload from "@/components/ArtworkUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public artwork upload page — customers reach this from their order confirmation email.
 * URL: /order/:id/artwork
 * We only expose the order's non-sensitive status fields (never buyer address / payment id).
 */
export default function OrderArtwork() {
  const { id } = useParams();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: "Upload artwork for your order",
    description: "Send us your print-ready PDF so we can start production.",
    path: `/order/${id}/artwork`,
  });

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/checkout/orders/${id}/status`);
      setStatus(data);
    } catch (err) {
      setStatus(null);
      setError(err.response?.status === 404 ? "Order not found." : "Could not load order status. Please check the link or contact us.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="pt-28 pb-24 bg-ink-950 min-h-screen" data-testid="order-artwork-page">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <nav className="text-[10px] uppercase tracking-[0.24em] text-white/40 mb-6 flex items-center gap-2">
          <Link to="/" className="hover:text-gold">Home</Link>
          <ChevronRight size={11} />
          <span className="text-gold">Upload artwork</span>
        </nav>

        <div className="text-center mb-10">
          <div className="eyebrow gold-bar mx-auto w-fit">Next step</div>
          <h1 className="font-display text-4xl md:text-5xl mt-4 tracking-tight" data-testid="artwork-title">
            Upload your artwork
          </h1>
          <p className="text-white/60 text-sm mt-4 max-w-xl mx-auto leading-relaxed">
            Send us your print-ready PDF so we can start production. Order <span className="font-mono text-gold">#{(id || "").slice(0, 8)}</span>
          </p>
        </div>

        {loading ? (
          <div className="card-dark p-14 text-center text-white/60 flex items-center justify-center gap-3">
            <Loader2 size={16} className="animate-spin text-gold" /> Loading order…
          </div>
        ) : error ? (
          <div className="card-dark p-8 flex items-start gap-4">
            <AlertCircle size={20} className="text-red-400 shrink-0 mt-0.5" />
            <div>
              <div className="font-display text-lg text-white">Something&apos;s off</div>
              <p className="text-white/60 text-sm mt-2">{error}</p>
              <p className="text-white/50 text-xs mt-4">
                Reach out to <a href="mailto:torontocustomt@gmail.com" className="text-gold hover:underline">torontocustomt@gmail.com</a> and we&apos;ll help sort it out.
              </p>
            </div>
          </div>
        ) : status?.payment_status !== "paid" ? (
          <div className="card-dark p-8 flex items-start gap-4" data-testid="unpaid-order">
            <AlertCircle size={20} className="text-gold shrink-0 mt-0.5" />
            <div>
              <div className="font-display text-lg text-white">Payment not yet completed</div>
              <p className="text-white/60 text-sm mt-2">
                We couldn&apos;t confirm payment for this order. If you just paid, refresh in a moment. If you need help, contact us.
              </p>
              <button onClick={refresh} className="mt-4 btn-ghost !py-2 !px-4 !text-[10px]">Refresh status</button>
            </div>
          </div>
        ) : (
          <div className="card-dark p-8" data-testid="artwork-panel">
            {/* Order summary strip */}
            <div className="flex items-center gap-4 pb-5 border-b border-white/5 mb-6">
              <div className="w-10 h-10 rounded-full bg-gold/15 border border-gold flex items-center justify-center shrink-0">
                <CheckCircle2 size={16} className="text-gold" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Payment confirmed</div>
                <div className="text-white/85 text-sm mt-0.5">
                  ${((status.total_cents || 0) / 100).toFixed(2)} {status.currency || "CAD"} · {status.line_count} item(s)
                </div>
              </div>
              <span className={`text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 border ${
                status.artwork_uploaded ? "bg-gold/20 text-gold border-gold/50" : "bg-purple/20 text-purple-hi border-purple-hi/40"
              }`}>
                {status.artwork_uploaded ? "artwork received" : "awaiting artwork"}
              </span>
            </div>

            <ArtworkUpload
              orderId={id}
              currentFile={status.artwork_uploaded ? { filename: status.artwork_filename, size: status.artwork_size } : null}
              onUploaded={(data) => setStatus((s) => s ? { ...s, artwork_uploaded: true, artwork_filename: data.filename, artwork_size: data.size } : s)}
            />
          </div>
        )}

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <TipCard title="Print-ready PDF" body="CMYK, 300 DPI, 1/16″ bleed, fonts outlined." />
          <TipCard title="Free artwork check" body="We review every file for print quality before production." />
          <TipCard title="Need help?" body={<>Email <a href="mailto:torontocustomt@gmail.com" className="text-gold hover:underline">torontocustomt@gmail.com</a></>} />
        </div>
      </div>
    </div>
  );
}

function TipCard({ title, body }) {
  return (
    <div className="card-dark p-5">
      <ShieldCheck size={14} className="text-gold mb-3" />
      <div className="font-display text-base text-white">{title}</div>
      <p className="text-white/55 text-xs mt-2 leading-relaxed">{body}</p>
    </div>
  );
}
